import {Component, CUSTOM_ELEMENTS_SCHEMA, inject, viewChild} from '@angular/core';
import {WjFlexSheet, WjGridSheetModule} from '@mescius/wijmo.angular2.grid.sheet';
import { PortfolioSegmentDataContext } from '../../data/portfolio-segment-data-context.service';
import {AppDataFormulaService} from '../../data/app-data-formula.service';
import {AccountDataAttribute, AccountGridDataType, DataDictionaryService} from '../../data/data-dictionary';
import {difference} from 'es-toolkit';
import {CollectionView, DataType} from '@mescius/wijmo';
import {AccountGridService} from '../account-grid/account-grid-helper';
import {Alerts} from '../../lib/alerts';
import {PortfolioSegmentWorkbook} from '../../data/portfolio-segment-workbook';
import {AppConstants} from '../../app.constants';
import {FormulaEvaluateTableColumn, FormulaEvaluateTableInput} from '../../../api-client-py/src';
import {AccountData} from '../../../api-client-kt/src';

const None = 'b5f11b66-a2f3-4874-b91e-2f6aee6116d2';

@Component({
    selector: 'app-app-dev-account-sheet',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        WjGridSheetModule
    ],
    templateUrl: './app-dev-account-sheet.component.html',
    styleUrl: './app-dev-account-sheet.component.scss'
})
export class AppDevAccountSheetComponent {

    private readonly pfSegData = inject(PortfolioSegmentDataContext);

    private readonly workbook = viewChild.required<WjFlexSheet>('workbook');

    initializeFlexSheet(flex: WjFlexSheet) {

    }

    static guessDataType(name: string, dd: AccountDataAttribute | undefined): DataType | null {
        if (dd) {
            return AppDevAccountSheetComponent.dataTypeToWjDataType(dd.pfmDataType);
        } else {
            if (/percentile/.test(name)) return DataType.Number;
            return null;
        }
    }

    static dataTypeToWjDataType(dataType: AccountGridDataType | undefined): DataType | null {
        if (!dataType) return DataType.Number;
        switch (dataType) {
            case 'text': return DataType.String;
            case 'number': return DataType.Number;
            case 'amount': return DataType.Number;
            case 'integer': return DataType.Number;
            case 'percentage': return DataType.Number;
            case 'date': return DataType.Date;
            case 'datetime': return DataType.Date;
            case 'boolean': return DataType.Boolean;
            default: throw new Error(`Unknown data type: ${dataType}`);
        }
    }

    onAddAccountsSheet(workbook: WjFlexSheet) {
        const accounts = this.pfSegData.getAccountData();
        const accountsData = structuredClone(accounts || []);
        const sheet = workbook.addBoundSheet("Accounts", accountsData);
        workbook.columns.getColumn('inceptionDate').format = 'yyyy-MM-dd';
    }

    onAddFormulaEvaluationSheet(workbook: WjFlexSheet) {
        const accounts = this.pfSegData.getAccountData();
        const kpiAttrs = this.pfSegData.segmentKpis.value()?.map(it => it.attribute) ?? [];
        const kpiAttrsPercWeights = PortfolioSegmentWorkbook.kpisToPercentileAndWeightAttributes(kpiAttrs);

        const attrsToSend = [...PortfolioSegmentWorkbook.AccountAttributesToSend, ...kpiAttrs];
        const attrsToReceive = [...PortfolioSegmentWorkbook.AccountAttributesToReceive, ...kpiAttrsPercWeights];

        const data = this.extractAccountDataToSend(accounts, kpiAttrs);

        // Patch first row to have all attributes present, because the FlexSheet
        // will only generate columns where at least one row has data for that column.
        const firstRow = data[0];
        this.patchNullValueOccupation(firstRow);

        const receiveOnlyColumns = difference(attrsToReceive, attrsToSend)
        const emptyResults = Object.fromEntries(receiveOnlyColumns.map(it => [it, None]));
        Object.assign(firstRow, emptyResults);

        workbook.addBoundSheet("PY Account Data", data);
        this.unpatchNullValueOccupation(firstRow);

        attrsToSend
            .forEach(name => {
                const column = workbook.columns.getColumn(name);
                const dd = DataDictionaryService.getAccountDataAttribute(name);
                const fmt = dd?.format ?? AccountGridService.getDisplayFormatForDataType(dd?.pfmDataType);
                if (fmt) column.format = fmt;
                column.dataType = AppDevAccountSheetComponent.guessDataType(name, dd);
                column.cssClass = 'formula-send-column';
            });

        attrsToReceive
            .forEach((name, i) => {
                const column = workbook.columns.getColumn(name);
                const dd = DataDictionaryService.getAccountDataAttribute(name);
                const fmt = dd?.format ?? AccountGridService.getDisplayFormatForDataType(dd?.pfmDataType);
                if (fmt) column.format = fmt;
                column.dataType = AppDevAccountSheetComponent.guessDataType(name, dd);
                column.cssClass = 'formula-receive-column';
            });
    }

    private extractAccountDataToSend(accounts: Partial<AccountData>[], extraAttributes: string[]): Record<string, any>[] {
        const allAttrs= [...PortfolioSegmentWorkbook.AccountAttributesToSend, ...extraAttributes] as (keyof AccountData)[];
        return accounts.map(account => {
            const data: Record<string, any> = {};
            for (const attr of allAttrs) {
                data[attr] = account[attr];
            }
            return data;
        });
    }


    private patchNullValueOccupation(row: any) {
        for (let key in row) {
            if (row[key] === undefined || row[key] === null) {
                row[key] = None;
            }
        }
    }

    private unpatchNullValueOccupation(row: { [p: string]: any }) {
        for (let key in row) {
            if (row[key] === None) {
                row[key] = null;
            }
        }
    }


    loadXlsx(fileInput: HTMLInputElement) {
        if (fileInput.files![0]) {
            this.workbook().loadAsync(fileInput.files![0]);
        }
    }

    saveXlsx(workbook: WjFlexSheet) {
        const fileName = 'FlexSheet.xlsx';
        workbook.saveAsync(fileName, undefined, undefined, { });
    }

    onAutosizeColumns(workbook: WjFlexSheet) {
        workbook.autoSizeColumns();
    }

    async onSendCurrentToPython(workbook: WjFlexSheet) {
        const pfSegWorkbook = this.pfSegData.wb;
        const accounts = this.workbook().collectionView.items as AccountData[];

        try {
            const response = await pfSegWorkbook.evaluateAccountFormulasAndChecks({
                accounts: accounts,
            });
            console.log(response?.debugInfo);

            const workbook = this.workbook();
            const activeSheet = workbook.selectedSheetIndex;

            workbook.sheets.filter(it => it.name.startsWith('DEBUG_'))
                .forEach(sheet => workbook.sheets.remove(sheet));

            response?.tables
                ?.filter(it => it.name !== AppConstants.PYFORMULA_ACCOUNTS_TABLE_NAME)
                ?.forEach(table => this.addDebugTable(workbook, table));
            workbook.selectedSheetIndex = activeSheet;

        } catch (e) {
            await Alerts.alertPopup({
                header: 'Error sending data to Python',
                body: `Error sending data to Python: ${e}`,
                width: '600px'
            });
        }
    }

    private objFromArrayData(properties: FormulaEvaluateTableColumn[], values: any[]):  { [p: string]: any } {
        const obj: { [key: string]: any } = {};
        properties.forEach((prop, i) => obj[prop.name] = values[i]);
        return obj;
    }

    private addDebugTable(workbook: WjFlexSheet, table: FormulaEvaluateTableInput) {
        const tableData = table.data.map((row: any[]) => this.objFromArrayData(table.columns, row));
        workbook.addBoundSheet(`DEBUG_${table.name}`, tableData);
    }
}
